import { useState, useEffect, useRef } from "react";
import toastError from "../../errors/toastError";

import api from "../../services/api";

const useTickets = ({
  searchParam,
  searchBy,
  tags,
  contactTags,
  users,
  pageNumber,
  status,
  date,
  updatedAt,
  showAll,
  queueIds,
  whatsappIds,
  withUnreadMessages,
  sortOrder,
  sortKey,
  refreshTrigger,
  enabled = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [tickets, setTickets] = useState([]);
  const latestRequestRef = useRef(0);
  const [alarmSummary, setAlarmSummary] = useState({
    enabled: false,
    minutes: 10,
    total: 0,
    open: 0,
    pending: 0,
  });

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    const correlationId = `tickets-${Date.now()}-${requestId}`;
    const abortController = new AbortController();
    const delayDebounceFn = setTimeout(() => {
      const fetchTickets = async () => {
        try {
          const { data } = await api.get("/tickets", {
            params: {
              searchParam,
              searchBy,
              pageNumber,
              tags,
              contactTags,
              users,
              status,
              date,
              updatedAt,
              showAll,
              queueIds,
              whatsappIds,
              withUnreadMessages,
              sortOrder,
            },
            headers: {
              "x-correlation-id": correlationId,
            },
            signal: abortController.signal,
            timeout: 30000,
          });
          if (latestRequestRef.current !== requestId) {
            return;
          }

          setTickets(Array.isArray(data?.tickets) ? data.tickets : []);
          setHasMore(!!data?.hasMore);
          setAlarmSummary(
            data.alarmSummary || {
              enabled: false,
              minutes: 10,
              total: 0,
              open: 0,
              pending: 0,
            }
          );
          setLoading(false);
        } catch (err) {
          if (err?.code === "ERR_CANCELED") {
            return;
          }

          if (latestRequestRef.current !== requestId) {
            return;
          }

          setLoading(false);
          toastError(err);
        }
      };
      fetchTickets();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
      abortController.abort();
    };
  }, [
    searchParam,
    searchBy,
    tags,
    contactTags,
    users,
    pageNumber,
    status,
    date,
    updatedAt,
    showAll,
    queueIds,
    whatsappIds,
    withUnreadMessages,
    sortOrder,
    sortKey,
    refreshTrigger,
    enabled,
  ]);

  return { tickets, loading, hasMore, alarmSummary };
};

export default useTickets;
